import { ACCEPTED_VIDEO_EXTENSIONS } from './constants';

/**
 * Extract a safe, normalized video extension from a user-supplied filename.
 *
 * Lowercases, strips anything after the extension (query junk, spaces), and
 * only returns a value if it maps to an accepted container. Returns `null` for
 * unsupported or missing extensions. Used both to validate uploads and to build
 * the S3 object key safely (the key is `externalUploads/{orgId}/{storyId}.{ext}`,
 * so `{ext}` is the only user-influenced segment).
 */
export function normalizeVideoExtension(fileName: string): string | null {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot === -1 || lastDot === fileName.length - 1) return null;

  const raw = fileName
    .slice(lastDot + 1)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

  return raw in ACCEPTED_VIDEO_EXTENSIONS ? raw : null;
}

/**
 * Safe extension for the S3 key, falling back to `mp4` when the source name has
 * no usable extension (the magic-byte tier has already confirmed it is a real
 * video container by the time this is used for keying).
 */
export function safeVideoExtension(fileName: string): string {
  return normalizeVideoExtension(fileName) ?? 'mp4';
}
